import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Mail, CheckCircle2, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useMembers } from '@/features/members/public';
import { PALETTE } from '@/core/lib/color-palette';
import type { Recipient } from '../types';

const MEMBERS_PAGE_SIZE = 20;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function deterministicColor(seed: string): string {
  const hash = seed.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

interface RecipientPickerProps {
  recipients: Recipient[];
  onChange: (recipients: Recipient[]) => void;
  disabled?: boolean;
}

export function RecipientPicker({ recipients, onChange, disabled = false }: RecipientPickerProps) {
  const { t } = useTranslation('forms');
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'member' | 'email'>('member');
  const [search, setSearch] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [pageSize, setPageSize] = useState(MEMBERS_PAGE_SIZE);
  const [lastSearch, setLastSearch] = useState(search);
  const debouncedSearch = useDebounce(search, 300);

  // A new search term invalidates how many results are "loaded so far" — restart
  // from the first page instead of keeping whatever page size scrolling had grown to.
  if (debouncedSearch !== lastSearch) {
    setLastSearch(debouncedSearch);
    setPageSize(MEMBERS_PAGE_SIZE);
  }

  const {
    data: knownMembers,
    totalCount,
    isLoading,
    isFetching,
  } = useMembers(debouncedSearch, 0, pageSize);

  const addedUserIds = new Set(recipients.filter((r) => r.type === 'Member').map((r) => r.userId));
  const addedEmails = new Set(recipients.filter((r) => r.type === 'Email').map((r) => r.email));
  const selectableMembers = knownMembers.filter((m) => !addedUserIds.has(m.userId));
  const hasMore = knownMembers.length < totalCount;

  function handleListScroll(e: React.UIEvent<HTMLDivElement>) {
    if (!hasMore || isFetching) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      setPageSize((n) => n + MEMBERS_PAGE_SIZE);
    }
  }

  function addMember(userId: string) {
    const member = knownMembers.find((m) => m.userId === userId);
    onChange([
      ...recipients,
      {
        userId,
        email: null,
        type: 'Member',
        token: null,
        tokenSentAt: null,
        respondedAt: null,
        displayName: member?.displayName ?? null,
        avatarUrl: member?.avatarUrl ?? null,
      },
    ]);
    setSearch('');
  }

  function addEmail() {
    const value = emailInput.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setEmailError(true);
      return;
    }
    if (addedEmails.has(value)) {
      setEmailInput('');
      return;
    }
    onChange([
      ...recipients,
      {
        userId: null,
        email: value,
        type: 'Email',
        token: null,
        tokenSentAt: null,
        respondedAt: null,
        displayName: null,
        avatarUrl: null,
      },
    ]);
    setEmailInput('');
    setEmailError(false);
  }

  function removeAt(index: number) {
    onChange(recipients.filter((_, i) => i !== index));
  }

  const respondedCount = recipients.filter((r) => r.respondedAt).length;

  const addRecipientPopover = !disabled && (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 rounded-full"
          data-testid="forms-recipient-picker-trigger"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {t('share.recipients.addButton')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setMode('member')}
            className={`flex-1 px-3 py-2 text-xs font-medium ${mode === 'member' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
            data-testid="forms-recipient-picker-tab-member"
          >
            {t('share.recipients.memberTab')}
          </button>
          <button
            type="button"
            onClick={() => setMode('email')}
            className={`flex-1 px-3 py-2 text-xs font-medium ${mode === 'email' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
            data-testid="forms-recipient-picker-tab-email"
          >
            {t('share.recipients.emailTab')}
          </button>
        </div>

        {mode === 'member' ? (
          <div className="p-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('share.recipients.searchPlaceholder')}
              className="h-8 text-xs mb-2"
              data-testid="forms-recipient-picker-search"
            />
            <Separator className="mb-1" />
            <div className="max-h-48 overflow-y-auto" onScroll={handleListScroll}>
              {isLoading ? (
                <div className="flex flex-col gap-1.5 px-1 py-1.5">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-6 w-full rounded" />
                  ))}
                </div>
              ) : selectableMembers.length === 0 ? (
                <p className="py-3 text-center text-xs text-muted-foreground">
                  {t('share.recipients.noResults')}
                </p>
              ) : (
                <>
                  {selectableMembers.map((m) => (
                    <button
                      type="button"
                      key={m.userId}
                      onClick={() => addMember(m.userId)}
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
                      data-testid={`forms-recipient-picker-member-${m.userId}`}
                    >
                      <UserAvatar
                        name={m.displayName}
                        color={deterministicColor(m.userId)}
                        src={m.avatarUrl ?? undefined}
                        size="xs"
                      />
                      <span className="flex-1 truncate text-left text-xs">{m.displayName}</span>
                    </button>
                  ))}
                  {isFetching && (
                    <div className="flex justify-center py-1.5">
                      <Skeleton className="h-3 w-16 rounded" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="p-2 space-y-1.5">
            <Input
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                setEmailError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addEmail();
                }
              }}
              placeholder={t('share.recipients.emailPlaceholder')}
              className="h-8 text-xs"
              data-testid="forms-recipient-picker-email-input"
            />
            {emailError && (
              <p className="text-xs text-destructive">{t('share.recipients.emailInvalid')}</p>
            )}
            <Button
              type="button"
              size="sm"
              className="w-full"
              onClick={addEmail}
              disabled={!emailInput.trim()}
              data-testid="forms-recipient-picker-email-add"
            >
              {t('share.recipients.addButton')}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="space-y-2" data-testid="forms-recipient-picker">
      <div className="flex flex-wrap items-center gap-2">
        {recipients.map((r, index) => {
          const label = r.type === 'Member' ? (r.displayName ?? r.userId ?? '') : (r.email ?? '');
          const statusHint = r.respondedAt
            ? t('share.recipients.respondedHint')
            : t('share.recipients.pendingHint');
          return (
            <Tooltip key={`${r.type}-${r.userId ?? r.email}-${index}`}>
              <TooltipTrigger asChild>
                <div className="group relative" data-testid={`forms-recipient-item-${index}`}>
                  {r.type === 'Member' ? (
                    <UserAvatar
                      name={label}
                      color={deterministicColor(r.userId ?? label)}
                      src={r.avatarUrl ?? undefined}
                      size="sm"
                    />
                  ) : (
                    <div className="flex size-6 items-center justify-center rounded-full bg-muted">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                    </div>
                  )}
                  <div className="absolute -bottom-0.5 -right-0.5 flex size-3 items-center justify-center rounded-full border-2 border-background bg-background">
                    {r.respondedAt ? (
                      <CheckCircle2 className="h-2.5 w-2.5 text-green-600" />
                    ) : (
                      <Clock className="h-2.5 w-2.5 text-amber-500" />
                    )}
                  </div>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeAt(index)}
                      className="absolute -top-1 -right-1 hidden size-3.5 items-center justify-center rounded-full bg-destructive text-white group-hover:flex"
                      aria-label={t('share.recipients.remove')}
                      data-testid={`forms-recipient-remove-${index}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {label} — {statusHint}
              </TooltipContent>
            </Tooltip>
          );
        })}

        {addRecipientPopover}
      </div>

      {recipients.length > 0 && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="forms-recipients-responded-summary"
        >
          {t('share.recipients.respondedSummary', {
            responded: respondedCount,
            total: recipients.length,
          })}
        </p>
      )}
    </div>
  );
}
